import { useTranslation } from "react-i18next";
import { Card, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import type { ModuleArtifact } from "./types";

type Props = {
	artifact: ModuleArtifact;
	selected?: boolean;
	onPress: (artifact: ModuleArtifact) => void;
};

export const ModuleArtifactCard = ({
	artifact,
	selected = false,
	onPress,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const isCompleted = artifact.status === "completed";
	const isNext = artifact.status === "next";
	const isLocked = artifact.status === "locked";
	const borderColor = selected
		? theme.colors.primary
		: isNext
			? theme.colors.primary
			: isLocked
				? theme.colors.outlineVariant
				: theme.colors.divider;

	return (
		<Card
			mode="outlined"
			onPress={() => onPress(artifact)}
			style={{
				borderColor,
				backgroundColor: theme.colors.surface,
			}}
		>
			<Card.Content>
				<Box flexDirection="row" alignItems="flex-start" gap={12}>
					<Box
						height={40}
						width={40}
						borderRadius={theme.roundness * 100}
						backgroundColor={theme.colors.secondaryContainer}
						alignItems="center"
						justifyContent="center"
					>
						<AppIcon
							name={artifact.iconName}
							size={20}
							color={theme.colors.onSecondaryContainer}
						/>
					</Box>
					<Box flex={1} gap={4}>
						<Text
							variant="labelMedium"
							numberOfLines={2}
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{artifact.statusLabel}
						</Text>
						<Text variant="bodyLarge" numberOfLines={2}>
							{artifact.title}
						</Text>
						<Text
							variant="bodyMedium"
							numberOfLines={3}
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{artifact.description}
						</Text>
					</Box>
					{isCompleted ? (
						<Box flexDirection="row" alignItems="center" gap={4}>
							<AppIcon
								name="check-circle"
								size={18}
								color={theme.colors.primary}
							/>
							<Text
								variant="labelMedium"
								style={{
									color: theme.colors.primary,
									fontWeight: "600",
									textTransform: "uppercase",
								}}
							>
								{t("lessons.detail.artifact.completed")}
							</Text>
						</Box>
					) : null}
					{isLocked ? (
						<Box flexDirection="row" alignItems="center" gap={4}>
							<AppIcon
								name="lock-outline"
								size={18}
								color={theme.colors.onSurfaceVariant}
							/>
							<Text
								variant="labelMedium"
								style={{
									color: theme.colors.onSurfaceVariant,
									fontWeight: "600",
									textTransform: "uppercase",
								}}
							>
								{t("lessons.detail.artifact.locked")}
							</Text>
						</Box>
					) : null}
				</Box>
			</Card.Content>
		</Card>
	);
};
